import type React from 'react'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Camera } from 'lucide-react'
import { toast } from 'sonner'
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import type { RootState } from '../../app/store/store'
import { fetchCompany } from '../../features/auth/authAPI'
import { fetchIndustries, fetchRegions, fetchCountries, fetchCities, clearCountries, clearCities } from '../../features/filters/filterSlice'
import { createCompany, updateCompany, updateCompanyDepartments } from '../../features/companies/companyAPI'
import { uploadFile, selectFile } from '../../utils/fileUploadUtils'
import { ProfileImage } from '../ProfileImage'
import FormDropdown from '../form-dropdown'
import PhoneCountrySelect from '../phone-country-select'
import CreateMemberForm from '../Profile-Setting-Sections/create-member-form'
import PhoneInput, {
    isValidPhoneNumber,
    isPossiblePhoneNumber,
    parsePhoneNumber,
    type Country,
} from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

interface CompanyFormData {
    companyName: string
    companyWebsite: string
    companyEmail: string
    companyPhone: string
    industryType: number | null
    city: number | null
    country: number | null
    addressLine1: string
    addressLine2: string
    aboutCompany: string
}

const getPhoneValidationCountry = (
    value: string,
    country?: Country,
): Country | undefined => {
    if (country) return country
    if (!value.trim()) return undefined
    try {
        return parsePhoneNumber(value)?.country
    } catch {
        return undefined
    }
}

const showInvalidPhoneHint = (value: string, country?: Country): boolean => {
    const trimmed = value.trim()
    if (!trimmed) return false

    const validationCountry = getPhoneValidationCountry(trimmed, country)
    if (!validationCountry) return false

    return (
        isPossiblePhoneNumber(trimmed, validationCountry) &&
        !isValidPhoneNumber(trimmed, validationCountry)
    )
}

const normalizeWebsiteUrl = (website: string): string => {
    const trimmedWebsite = website.trim()
    if (!trimmedWebsite) return ''

    if (/^https?:\/\//i.test(trimmedWebsite)) {
        return trimmedWebsite
    }

    return `https://${trimmedWebsite}`
}

const CompanyProfile: React.FC = () => {
    const { id } = useParams<{ id?: string }>()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const accessToken = useAppSelector((state: RootState) => state.auth.accessToken)

    const [activeTab, setActiveTab] = useState<'company' | 'user'>('company')
    const isEditMode = !!id

    // Reset to company tab if user tab is active but no company ID exists
    useEffect(() => {
        if (activeTab === 'user' && !id) {
            setActiveTab('company')
        }
    }, [activeTab, id])

    // Company form state
    const [companyFormData, setCompanyFormData] = useState<CompanyFormData>({
        companyName: '',
        companyWebsite: '',
        companyEmail: '',
        companyPhone: '',
        industryType: null,
        city: null,
        country: null,
        addressLine1: '',
        addressLine2: '',
        aboutCompany: '',
    })
    const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null)
    const [companyProfilePictureId, setCompanyProfilePictureId] = useState<number | null>(null)
    const [companyProfilePicturePreview, setCompanyProfilePicturePreview] = useState<string | null>(null)
    const [isCompanyUploading, setIsCompanyUploading] = useState(false)
    const [isCompanySaving, setIsCompanySaving] = useState(false)
    const [companyPhoneCountry, setCompanyPhoneCountry] = useState<Country | undefined>(undefined)

    const [userProfilePicturePreview] = useState<string | null>(null)

    // Redux selectors
    const industries = useAppSelector((state) => state.filters.industries)
    const industriesStatus = useAppSelector((state) => state.filters.industriesStatus)
    const regions = useAppSelector((state) => state.filters.regions)
    const regionsStatus = useAppSelector((state) => state.filters.regionsStatus)
    const countries = useAppSelector((state) => state.filters.countries)
    const countriesStatus = useAppSelector((state) => state.filters.countriesStatus)
    const cities = useAppSelector((state) => state.filters.cities)
    const citiesStatus = useAppSelector((state) => state.filters.citiesStatus)

    // Fetch filters on mount
    useEffect(() => {
        dispatch(fetchIndustries())
        dispatch(fetchRegions())
    }, [dispatch])

    // Fetch countries when region is selected
    useEffect(() => {
        if (selectedRegionId) {
            dispatch(fetchCountries({ regionId: selectedRegionId }))
        } else {
            dispatch(clearCountries())
            setCompanyFormData((prev) => ({ ...prev, country: null, city: null }))
        }
    }, [selectedRegionId, dispatch])

    // Fetch cities when country is selected
    useEffect(() => {
        if (companyFormData.country) {
            dispatch(fetchCities({ countryId: companyFormData.country }))
        } else {
            dispatch(clearCities())
            setCompanyFormData((prev) => ({ ...prev, city: null }))
        }
    }, [companyFormData.country, dispatch])

    // Fetch company data if in edit mode
    useEffect(() => {
        if (!isEditMode || !id || !accessToken) return

        let isMounted = true
        const loadCompanyData = async () => {
            try {
                const companyResponse = await fetchCompany(Number(id), accessToken)
                if (!isMounted) return

                const data = companyResponse.data
                if (!data) return

                // Extract IDs from nested objects
                const regionId = data.region?.id || null
                const countryId = data.country?.id || null
                const cityId = data.city?.id || null
                const industryId = data.industry?.id || null

                const rawContact = (data.contact_number || '').trim()
                let companyPhone = rawContact
                let resolvedPhoneCountry: Country | undefined
                if (rawContact) {
                    try {
                        const parsed = parsePhoneNumber(rawContact)
                        if (parsed?.isValid()) {
                            companyPhone = parsed.number
                        }
                        resolvedPhoneCountry = parsed?.country
                    } catch {
                        resolvedPhoneCountry = undefined
                    }
                }
                setCompanyPhoneCountry(resolvedPhoneCountry)

                // Set basic form data
                setCompanyFormData({
                    companyName: data.name || '',
                    companyWebsite: data.website || '',
                    companyEmail: data.email || '',
                    companyPhone,
                    industryType: industryId,
                    city: cityId,
                    country: countryId,
                    addressLine1: data.address_line_1 || '',
                    addressLine2: data.address_line_2 || '',
                    aboutCompany: data.about || '',
                })

                // Set region first - this will trigger fetching countries
                if (regionId) {
                    setSelectedRegionId(regionId)
                    // Fetch countries for this region
                    dispatch(fetchCountries({ regionId }))
                }

                // Set profile picture
                if (data.profile_picture) {
                    if (typeof data.profile_picture === 'number') {
                        setCompanyProfilePictureId(data.profile_picture)
                    }
                }
            } catch (error) {
                console.error('Failed to fetch company data:', error)
                toast.error('Failed to load company data')
            }
        }

        loadCompanyData()

        return () => {
            isMounted = false
        }
    }, [isEditMode, id, accessToken, dispatch])

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            if (companyProfilePicturePreview) {
                URL.revokeObjectURL(companyProfilePicturePreview)
            }
            if (userProfilePicturePreview) {
                URL.revokeObjectURL(userProfilePicturePreview)
            }
        }
    }, [companyProfilePicturePreview, userProfilePicturePreview])

    // Company form handlers
    const handleCompanyInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target
        if (name === 'region') {
            setSelectedRegionId(value === '' ? null : Number(value))
            return
        }
        setCompanyFormData((prev) => ({
            ...prev,
            [name]: name === 'industryType' || name === 'city' || name === 'country'
                ? (value === '' ? null : Number(value))
                : value,
        }))
    }

    const handleCompanyImageUpload = async () => {
        if (!accessToken) {
            toast.error('Authentication required to upload image')
            return
        }

        try {
            const file = await selectFile('image/*')
            if (!file) return

            if (!file.type.startsWith('image/')) {
                toast.error('Please select a valid image file')
                return
            }

            const maxSize = 10 * 1024 * 1024 // 10MB
            if (file.size > maxSize) {
                toast.error('Image size must be less than 10MB')
                return
            }

            setIsCompanyUploading(true)

            const previewUrl = URL.createObjectURL(file)
            setCompanyProfilePicturePreview(previewUrl)

            const fileId = await uploadFile(file, accessToken)

            if (fileId) {
                setCompanyProfilePictureId(fileId)
                toast.success('Image uploaded successfully')
            } else {
                setCompanyProfilePicturePreview(null)
                toast.error('Failed to upload image. Please try again.')
            }
        } catch (error) {
            console.error('Error uploading image:', error)
            setCompanyProfilePicturePreview(null)
            toast.error('Failed to upload image. Please try again.')
        } finally {
            setIsCompanyUploading(false)
        }
    }

    const handleCompanySave = async () => {
        if (!accessToken) {
            toast.error('Authentication required')
            return
        }

        if (!companyFormData.companyName.trim()) {
            toast.error('Company name is required')
            return
        }

        if (showInvalidPhoneHint(companyFormData.companyPhone, companyPhoneCountry)) {
            toast.error('Please enter a valid company contact number')
            return
        }

        try {
            setIsCompanySaving(true)
            const normalizedWebsite = normalizeWebsiteUrl(companyFormData.companyWebsite)

            const payload = {
                name: companyFormData.companyName,
                website: normalizedWebsite || null,
                email: companyFormData.companyEmail || null,
                contact_number: companyFormData.companyPhone || null,
                industry: companyFormData.industryType || null,
                country: companyFormData.country || null,
                city: companyFormData.city || null,
                address_line_1: companyFormData.addressLine1 || null,
                address_line_2: companyFormData.addressLine2 || null,
                about: companyFormData.aboutCompany || null,
                profile_picture: companyProfilePictureId || null,
            }

            if (isEditMode && id) {
                const result = await updateCompany(Number(id), payload, accessToken)
                if (result.error) {
                    toast.error(result.error)
                } else {
                    toast.success('Company updated successfully')
                    navigate('/company-list')
                }
            } else {
                const result = await createCompany(payload, accessToken)
                if (result.error) {
                    toast.error(result.error)
                } else {
                    // After successful company creation, update company departments
                    if (result.data?.id) {
                        const deptResult = await updateCompanyDepartments(result.data.id, accessToken)
                        if (deptResult.error) {
                            console.error('Failed to update company departments:', deptResult.error)
                            // Don't show error to user as company was created successfully
                        }
                    }
                    toast.success('Company created successfully')
                    navigate('/company-list')
                }
            }
        } catch (error: unknown) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to save company. Please try again.'
            toast.error(message)
        } finally {
            setIsCompanySaving(false)
        }
    }

    const isCompanyFormValid = () => {
        return (
            !!companyFormData.companyName.trim() &&
            !!companyFormData.companyEmail.trim() &&
            !showInvalidPhoneHint(companyFormData.companyPhone, companyPhoneCountry)
        )
    }

    return (
        <div className="space-y-6">
            {/* Tabs */}
            <div className="flex gap-4 border-gray-200">
                <button
                    type="button"
                    onClick={() => setActiveTab('company')}
                    className={`border border-gray-200 px-2 py-1 rounded-md drop-shadow-sm font-medium transition-colors ${activeTab === 'company'
                        ? 'text-gray-900 font-bold shadow-[0_0_6px_0px_rgba(0,0,0,0.2)]'
                        : 'text-[#ADA7A7] hover:text-gray-700'
                        }`}
                >
                    {isEditMode ? 'Edit Company' : 'Company Profile'}
                </button>
                {isEditMode && id && (
                    <button
                        type="button"
                        onClick={() => setActiveTab('user')}
                        className={`border border-gray-200 px-2 py-1 rounded-md drop-shadow-sm font-medium transition-colors ${activeTab === 'user'
                            ? 'text-gray-900 font-bold shadow-[0_0_6px_0px_rgba(0,0,0,0.2)]'
                            : 'text-[#ADA7A7] hover:text-gray-700'
                            }`}
                    >
                        Add a User
                    </button>
                )}
            </div>

            {/* Company Form */}
            {activeTab === 'company' && (
                <div className="space-y-6">
                    {/* Profile Photo Section */}
                    <div className="flex items-center gap-4 mb-8">
                        <div className="relative">
                            <div className="w-24 h-24 bg-gray-400 border-2 border-black rounded-full flex items-center justify-center overflow-hidden">
                                {companyProfilePicturePreview ? (
                                    <img
                                        src={companyProfilePicturePreview}
                                        alt="Company preview"
                                        className="w-24 h-24 rounded-full object-cover"
                                    />
                                ) : companyProfilePictureId ? (
                                    <ProfileImage
                                        profilePicture={companyProfilePictureId}
                                        accessToken={accessToken || undefined}
                                        alt="Company"
                                        className="w-24 h-24 rounded-full object-cover"
                                        fallbackPath="/assets/images/user-Avatar.png"
                                    />
                                ) : (
                                    <img
                                        src="/assets/images/user-Avatar.png"
                                        alt="Company placeholder"
                                        className="w-24 h-24 rounded-full object-cover"
                                    />
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={handleCompanyImageUpload}
                                disabled={isCompanyUploading || !accessToken}
                                className="absolute bottom-0 right-0 bg-[#ABD405] p-2 rounded-full border-2 border-black cursor-pointer hover:bg-lime-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Upload Photo"
                            >
                                {isCompanyUploading ? (
                                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-md animate-spin" />
                                ) : (
                                    <Camera size={16} className="text-[#000000]" />
                                )}
                            </button>
                        </div>
                        <div>
                            <p className="font-medium text-[#000000]">Upload company photo</p>
                            {isCompanyUploading && (
                                <p className="text-sm text-gray-500">Uploading...</p>
                            )}
                        </div>
                    </div>

                    {/* Company Name and Website */}
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-[#000000] mb-2">
                                Company name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="companyName"
                                value={companyFormData.companyName}
                                onChange={handleCompanyInputChange}
                                className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                                placeholder="Enter company name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-[#000000] mb-2">
                                Company website
                            </label>
                            <input
                                type="text"
                                name="companyWebsite"
                                value={companyFormData.companyWebsite}
                                onChange={handleCompanyInputChange}
                                className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                                placeholder="Enter company website"
                            />
                        </div>
                    </div>

                    {/* Company Email and Phone */}
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-[#000000] mb-2">
                                Company email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="companyEmail"
                                value={companyFormData.companyEmail}
                                onChange={handleCompanyInputChange}
                                className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                                placeholder="Enter company email"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-[#000000] mb-2">
                                Company contact number
                            </label>
                            <PhoneInput
                                international
                                country={companyPhoneCountry}
                                onCountryChange={setCompanyPhoneCountry}
                                countrySelectComponent={PhoneCountrySelect}
                                value={companyFormData.companyPhone}
                                onChange={(val) =>
                                    setCompanyFormData((prev) => ({
                                        ...prev,
                                        companyPhone: val ?? '',
                                    }))
                                }
                                className={`phone-input-wrapper ${companyFormData.companyPhone ? 'text-gray-800' : 'text-gray-400'}`}
                            />
                            {showInvalidPhoneHint(companyFormData.companyPhone, companyPhoneCountry) && (
                                <div className="text-red-500 text-sm">
                                    Please enter a valid phone number
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Region and Industry Type */}
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <FormDropdown
                                label="Region"
                                value={selectedRegionId ? String(selectedRegionId) : ''}
                                placeholder="Select region"
                                disabled={regionsStatus === 'loading'}
                                options={regions.map((region) => ({
                                    label: region.name,
                                    value: String(region.id),
                                }))}
                                onSelect={(value) => setSelectedRegionId(value === '' ? null : Number(value))}
                            />
                        </div>

                        <div>
                            <FormDropdown
                                label="Industry type"
                                value={companyFormData.industryType ? String(companyFormData.industryType) : ''}
                                placeholder="Select industry"
                                disabled={industriesStatus === 'loading'}
                                options={industries.map((industry) => ({
                                    label: industry.name,
                                    value: String(industry.id),
                                }))}
                                onSelect={(value) =>
                                    setCompanyFormData((prev) => ({
                                        ...prev,
                                        industryType: value === '' ? null : Number(value),
                                    }))
                                }
                            />
                        </div>
                    </div>

                    {/* Country and City */}
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <FormDropdown
                                label="Country"
                                value={companyFormData.country ? String(companyFormData.country) : ''}
                                placeholder={!selectedRegionId ? 'Select region first' : 'Select country'}
                                disabled={!selectedRegionId || countriesStatus === 'loading'}
                                options={countries.map((country) => ({
                                    label: country.name,
                                    value: String(country.id),
                                }))}
                                onSelect={(value) =>
                                    setCompanyFormData((prev) => ({
                                        ...prev,
                                        country: value === '' ? null : Number(value),
                                    }))
                                }
                            />
                        </div>

                        <div>
                            <FormDropdown
                                label="City"
                                value={companyFormData.city ? String(companyFormData.city) : ''}
                                placeholder={!companyFormData.country ? 'Select country first' : 'Select city'}
                                disabled={!companyFormData.country || citiesStatus === 'loading'}
                                options={cities.map((city) => ({
                                    label: city.name,
                                    value: String(city.id),
                                }))}
                                onSelect={(value) =>
                                    setCompanyFormData((prev) => ({
                                        ...prev,
                                        city: value === '' ? null : Number(value),
                                    }))
                                }
                            />
                        </div>
                    </div>

                    {/* Address Line 1 */}
                    <div>
                        <label className="block text-sm font-medium text-[#000000] mb-2">
                            Address line 1
                        </label>
                        <input
                            type="text"
                            name="addressLine1"
                            value={companyFormData.addressLine1}
                            onChange={handleCompanyInputChange}
                            className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                            placeholder="Enter address line 1"
                        />
                    </div>

                    {/* Address Line 2 */}
                    <div>
                        <label className="block text-sm font-medium text-[#000000] mb-2">
                            Address line 2
                        </label>
                        <input
                            type="text"
                            name="addressLine2"
                            value={companyFormData.addressLine2}
                            onChange={handleCompanyInputChange}
                            className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                            placeholder="Enter address line 2"
                        />
                    </div>

                    {/* About Company */}
                    <div>
                        <label className="block text-sm font-medium text-[#000000] mb-2">
                            About company
                        </label>
                        <textarea
                            name="aboutCompany"
                            value={companyFormData.aboutCompany}
                            onChange={handleCompanyInputChange}
                            rows={4}
                            className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 resize-none"
                            placeholder="Enter company description..."
                        />
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-4">
                        <button
                            type="button"
                            onClick={handleCompanySave}
                            disabled={isCompanySaving || !isCompanyFormValid()}
                            className="btn-cta px-8 py-2 font-semibold rounded-md disabled:opacity-60"
                        >
                            {isCompanySaving ? 'Saving...' : isEditMode ? 'Update Company' : 'Create Company'}
                        </button>
                    </div>
                </div>
            )}

            {/* User Form */}
            {activeTab === 'user' && (
                <CreateMemberForm
                    fixedCompanyId={id ? Number(id) : undefined}
                    redirectOnSuccess={false}
                    createButtonLabel="Create User"
                />
            )}
        </div>
    )
}

export default CompanyProfile